import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNow, isPast } from 'date-fns';
import { ChevronRight, Swords } from 'lucide-react';

import { EmptyState } from '@/components/EmptyState';
import { Badge } from '@/components/ui/badge';
import { useCurrentBatch } from '@/lib/hooks/useCurrentBatch';
import { useStudentHackathons, useStudentHackathonsAllBatches } from '@/lib/hooks/useHackathons';
import type { Hackathon, HackathonStatus } from '@/lib/api/hackathons';

export const Route = createFileRoute('/_student/hackathons/')({ component: StudentHackathonsPage });

// Mirrors the STATUS_VARIANT mapping used by the trainer and org-admin
// hackathon surfaces, so the same lifecycle status renders identically
// everywhere in the app.
const STATUS_VARIANT: Record<HackathonStatus, 'default' | 'secondary' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  live: 'default',
  completed: 'secondary',
};

// A small colored dot gives an at-a-glance signal (matches the dot+label
// convention used for due-date states elsewhere in the student app) without
// duplicating the shadcn Badge's own "live" text — kept deliberately silent
// on wording so it never collides with the Badge in tests or on screen.
const STATUS_DOT: Record<HackathonStatus, string> = {
  draft: 'bg-[#c0b8b0]',
  scheduled: 'bg-amber-400',
  live: 'bg-emerald-500 animate-pulse',
  completed: 'bg-[#c0b8b0]',
};

// Subtle left-edge accent per status, echoing the semantic-color role each
// status plays (green = happening now, amber = coming up) rather than a flat
// gray card for every row regardless of urgency.
const STATUS_ACCENT: Record<HackathonStatus, string> = {
  draft: 'border-l-[#e8e4df]',
  scheduled: 'border-l-amber-300',
  live: 'border-l-emerald-400',
  completed: 'border-l-[#e8e4df]',
};

// One-line, glanceable urgency cue per card — a raw date range doesn't tell
// a student "is this happening now / can I still join" at a glance.
function timingLabel(h: Hackathon): string {
  const starts = new Date(h.start_at);
  const ends = new Date(h.end_at);
  switch (h.status) {
    case 'draft':
      return 'Not open yet';
    case 'scheduled':
      return isPast(starts) ? 'Starting soon' : `Starts in ${formatDistanceToNow(starts)}`;
    case 'live':
      return isPast(ends) ? 'Wrapping up' : `${formatDistanceToNow(ends)} left`;
    case 'completed':
      return `Ended ${formatDistanceToNow(ends)} ago`;
  }
}

export function StudentHackathonsPage() {
  const { batchId, isAll, batches } = useCurrentBatch();

  // "All Courses" has no server-side aggregate endpoint for hackathons (unlike
  // useHome/useUpcoming), so it fans out one request per enrolled batch and
  // merges the results — never silently falls back to a single batch.
  const allBatchesResult = useStudentHackathonsAllBatches(isAll ? batches.map((b) => b.id) : []);
  const singleBatchResult = useStudentHackathons(isAll ? '' : (batchId ?? ''));
  const { data: hackathons, isLoading } = isAll ? allBatchesResult : singleBatchResult;

  const batchNameById = new Map(batches.map((b) => [b.id, b.name]));

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1200px] p-6" aria-busy="true">
        <h1 className="mb-4 text-xl font-semibold text-foreground">Hackathons</h1>
        <div className="grid gap-3" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-[76px] animate-pulse rounded-lg border border-border bg-muted/40" />
          ))}
        </div>
      </div>
    );
  }

  if (!hackathons?.length) {
    return (
      <div className="mx-auto max-w-[1200px] p-6">
        <EmptyState
          icon={Swords}
          title="No hackathons yet"
          description={
            isAll
              ? "Your trainers haven't scheduled a hackathon for any of your batches yet."
              : "Your trainer hasn't scheduled a hackathon for this batch yet."
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[900px] p-6">
      <div className="mb-5">
        <h1 className="font-heading text-xl font-bold text-foreground">Hackathons</h1>
        <p className="text-sm text-muted-foreground">Compete with your batchmates and climb the leaderboard.</p>
      </div>
      <ul className="grid list-none gap-3 p-0">
        {hackathons.map((h) => (
          <li key={h.id}>
            <Link
              to="/hackathons/$hackathonId"
              params={{ hackathonId: h.id }}
              search={{ batchId: h.batch_id }}
              className={`group flex items-center justify-between gap-3 rounded-2xl border border-[#e8e4df] ${STATUS_ACCENT[h.status] ?? STATUS_ACCENT.draft} border-l-[3px] bg-white p-4 transition-all hover:border-foreground/20 hover:shadow-sm`}
            >
              <div className="min-w-0">
                <p className="truncate font-heading text-base font-bold text-foreground">{h.title}</p>
                <div className="mt-1 flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[h.status] ?? STATUS_DOT.draft}`} />
                  <p className="text-xs text-muted-foreground">
                    {timingLabel(h)}
                    {isAll && batchNameById.has(h.batch_id) && ` · ${batchNameById.get(h.batch_id)}`}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge
                  variant={STATUS_VARIANT[h.status] ?? STATUS_VARIANT.draft}
                  className="capitalize"
                >
                  {h.status}
                </Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
